import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  inject,
  input,
  model,
  signal,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import {
  FileSlot,
  FileViewerService,
  PageHeader,
  nombreDesdeContentDisposition,
  type FileSlotReject,
  type FileSlotState,
} from '@intelishs-group/ui';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzMessageService } from 'ng-zorro-antd/message';
import { EmpleadoList, EmpleadoDocumento } from '@schemas/empleado.schema';
import {
  TIPOS_DOCUMENTO_RH,
  TipoDocumentoRH,
} from '@shared/constants/tipos-documento-rh.constants';
import { EmpleadoService } from '@services/empleado.service';

/**
 * Estado por tipo de documento dentro del modal. Si una key no está en el
 * record, ese tipo no tiene documento cargado.
 */
interface DocSlot {
  /** kiddocumento en el FileManager, o null mientras solo se está subiendo. */
  kidDocumento: number | null;
  /** true mientras sube o elimina (bloquea la fila). */
  busy: boolean;
  /** true mientras descarga para visualizar. */
  downloading: boolean;
}

/**
 * Modal "Documentos del Empleado" — conectado al FileManager real.
 *
 * Flujo: el navegador NO habla directo con el FileManager; sube/baja/borra
 * contra el backend de RH (`/api/Empleado/{id}/documentos`), que proxquea al
 * microservicio (AWS S3) y guarda la referencia `{ tipo, kiddocumento }` en
 * `catempleadorh.jdatos.archivos`. Cada tipo de TIPOS_DOCUMENTO_RH es una fila;
 * `tipo` es el `kidtipodocumento` real del catálogo del FileManager.
 *
 * **Este componente es local solo por su dominio.** Cada fila es una
 * `<intelishs-file-slot>` de `@intelishs-group/ui`: de ahí vienen los cuatro
 * estados, el arrastre, la validación por `accept`/`maxMb` y el `<input>` oculto.
 * Aquí se queda el transporte, el catálogo de tipos, la confirmación destructiva
 * y los textos de aviso —el mensaje es del dominio—. No reimplementar el
 * arrastre: la ranura cubre tres trampas que fallan en silencio (el `accept` del
 * navegador no valida lo que se suelta, sin `preventDefault` el navegador abre el
 * archivo y tira lo capturado, y re-elegir el mismo archivo no emite `change`).
 */
@Component({
  selector: 'app-documentos-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileSlot, PageHeader, NzButtonModule, NzModalModule, NzIconModule, NzSpinModule],
  templateUrl: './documentos-modal.html',
  styleUrl: './documentos-modal.css',
})
export class DocumentosModal {
  private readonly message = inject(NzMessageService);
  private readonly empleadoService = inject(EmpleadoService);
  private readonly fileViewer = inject(FileViewerService);
  private readonly destroyRef = inject(DestroyRef);

  visible = model(false);
  empleado = input<EmpleadoList | null>(null);

  /**
   * El contexto va en el SUBTÍTULO, nunca en el título, y se arma aquí porque
   * `page-header` recibe un string: con partes opcionales, el encadenado se
   * lee peor en el template.
   */
  subtitulo = computed(() => {
    const emp = this.empleado();
    if (!emp) return '';
    return `${emp.nombreCompleto} · ${emp.idInterno} · ${this.cargados()} de ${this.totalTipos} cargados`;
  });

  readonly tipos: ReadonlyArray<TipoDocumentoRH> = TIPOS_DOCUMENTO_RH;

  /** Estado por tipo de documento (kiddocumento + flags de carga). */
  slots = signal<Record<number, DocSlot>>({});

  /**
   * Nombre real del archivo por tipo, cuando lo conocemos: se llena al subir
   * (tenemos `file.name`) y al visualizar (lo trae `Content-Disposition`).
   * Vive aparte de `slots` porque `aplicarLista()` reconstruye los slots desde
   * la respuesta del backend, que solo devuelve `{ tipo, kidDocumento }`.
   */
  fileNames = signal<Record<number, string>>({});

  /** true mientras se cargan los documentos al abrir el modal. */
  loading = signal(false);

  /** Cuántos tipos tienen documento — alimenta el contador del encabezado. */
  cargados = computed(
    () => Object.values(this.slots()).filter((s) => s.kidDocumento != null).length,
  );

  readonly totalTipos = TIPOS_DOCUMENTO_RH.length;

  /**
   * Formatos y tope admitidos, iguales para los 14 tipos. Se pasan a cada ranura
   * —`accept` y `maxMb` son por ranura, no globales— así que el día que un tipo
   * pida algo distinto (solo XML para un CFDI, solo imagen para la foto) basta
   * moverlos al catálogo sin tocar el componente.
   *
   * El tope debe coincidir con el del FileManager, que corta en 10 MB.
   */
  static readonly ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx,.doc';
  static readonly MAX_MB = 10;

  protected readonly accept = DocumentosModal.ACCEPT;
  protected readonly maxMb = DocumentosModal.MAX_MB;

  constructor() {
    // Al abrir, cargar los documentos del empleado; al cerrar, limpiar.
    effect(() => {
      if (this.visible() && this.empleadoId > 0) {
        this.cargarDocumentos();
      } else if (!this.visible()) {
        this.slots.set({});
        this.fileNames.set({});
      }
    });
  }

  private get empleadoId(): number {
    return this.empleado()?.id ?? 0;
  }

  // ── Lo que consume la ranura ──────────────────────────────────────

  /**
   * Traduce el estado del módulo a los cuatro estados de la fila. La ranura no
   * lo deduce: son excluyentes justamente para que no se pueda pintar la
   * palomita de "cargado" mientras la fila dice "Procesando".
   */
  estado(tipoId: number): FileSlotState {
    const slot = this.slots()[tipoId];
    if (slot?.busy) return 'subiendo';
    if (!slot?.kidDocumento) return 'vacio';
    return slot.downloading ? 'abriendo' : 'cargado';
  }

  /** Nombre real del archivo si lo conocemos; si no, la ranura pone el suyo. */
  nombre(tipoId: number): string {
    return this.fileNames()[tipoId] ?? '';
  }

  /** El aviso lo da el modal: la ranura solo reporta qué descartó y por qué. */
  avisar(r: FileSlotReject): void {
    if (r.reason === 'tamano') {
      this.message.warning(`El archivo excede ${DocumentosModal.MAX_MB} MB.`);
    } else if (r.reason === 'extension') {
      this.message.warning('Formato no admitido: PDF, imagen, Word o Excel.');
    } else {
      this.message.info('Cada tipo admite un archivo: se tomó el primero.');
    }
  }

  onAccion(accion: string, tipo: TipoDocumentoRH): void {
    // `reemplazar` no se atiende: la ranura abre su propio selector y lo que
    // llegue vuelve por `(pick)`.
    if (accion === 'ver') this.ver(tipo.id);
    else if (accion === 'eliminar') this.confirmarEliminar(tipo);
  }

  // ── Acciones ──────────────────────────────────────────────────────

  /**
   * Alta del archivo. Llega por `(pick)` de la ranura, así que ya pasó la
   * validación de extensión y tamaño: aquí solo queda el transporte.
   */
  subir(tipoId: number, file: File): void {
    if (this.empleadoId <= 0) {
      this.message.error('No se pudo identificar al empleado.');
      return;
    }

    this.patchSlot(tipoId, { busy: true });
    this.empleadoService
      .subirDocumento(this.empleadoId, tipoId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lista) => {
          // Guardar el nombre antes de aplicar la lista: el backend solo
          // devuelve { tipo, kidDocumento }, así que este es el único momento
          // en que conocemos el nombre real sin volver a descargar el archivo.
          this.recordarNombre(tipoId, file.name);
          this.aplicarLista(lista);
          this.message.success(`Documento subido: ${file.name}`);
        },
        error: (err) => {
          this.patchSlot(tipoId, { busy: false });
          this.message.error(this.extraerError(err) ?? 'No se pudo subir el documento.');
        },
      });
  }

  /**
   * Descarga el documento y lo entrega al visor compartido
   * (`<intelishs-file-viewer>` de @intelishs-group/ui, montado una sola vez
   * en `app.html`), que decide cómo renderizarlo según el MIME: PDF en iframe,
   * imágenes inline, XML formateado, y para el resto (xlsx/docx) un botón de
   * descarga.
   */
  ver(tipoId: number): void {
    const slot = this.slots()[tipoId];
    if (!slot?.kidDocumento || this.empleadoId <= 0) return;

    this.patchSlot(tipoId, { downloading: true });
    this.empleadoService
      .descargarDocumento(this.empleadoId, slot.kidDocumento)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.patchSlot(tipoId, { downloading: false });
          const blob = resp.body;
          if (!blob) {
            this.message.error('El documento llegó vacío.');
            return;
          }
          // El nombre real viaja en el header, no en el cuerpo, y solo llega si
          // el backend lo expone por CORS (`.WithExposedHeaders`). Si no, cae al
          // nombre del tipo de documento.
          const nombre = nombreDesdeContentDisposition(
            resp.headers.get('Content-Disposition'),
            this.tipos.find((t) => t.id === tipoId)?.nombre ?? `documento_${tipoId}`,
          );
          this.recordarNombre(tipoId, nombre);
          this.fileViewer.showPreview(blob, nombre);
        },
        error: () => {
          this.patchSlot(tipoId, { downloading: false });
          this.message.error('No se pudo descargar el documento.');
        },
      });
  }

  /**
   * Confirmación con SweetAlert y no con `nz-popconfirm`: la botonera de la
   * ranura emite la acción y no expone el botón para colgarle un overlay. Es
   * además el mecanismo que ya usa el listado de empleados para desactivar, así
   * que las confirmaciones destructivas se ven igual en todo el módulo.
   */
  private async confirmarEliminar(tipo: TipoDocumentoRH): Promise<void> {
    const r = await Swal.fire({
      title: '¿Eliminar este documento?',
      text: tipo.nombre,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (r.isConfirmed) this.eliminar(tipo.id);
  }

  /** Elimina (borrado lógico) el documento del tipo indicado. */
  eliminar(tipoId: number): void {
    const slot = this.slots()[tipoId];
    if (!slot?.kidDocumento || this.empleadoId <= 0) return;

    this.patchSlot(tipoId, { busy: true });
    this.empleadoService
      .eliminarDocumento(this.empleadoId, slot.kidDocumento)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lista) => {
          this.aplicarLista(lista);
          this.message.info('Documento eliminado.');
        },
        error: () => {
          this.patchSlot(tipoId, { busy: false });
          this.message.error('No se pudo eliminar el documento.');
        },
      });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  // ── Privados ──────────────────────────────────────────────────────

  private cargarDocumentos(): void {
    if (this.empleadoId <= 0) {
      this.slots.set({});
      return;
    }
    this.loading.set(true);
    this.empleadoService
      .getDocumentos(this.empleadoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lista) => {
          this.aplicarLista(lista);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.message.error('No se pudieron cargar los documentos del empleado.');
        },
      });
  }

  /** Reemplaza todo el estado a partir de la lista que devuelve el backend. */
  private aplicarLista(lista: EmpleadoDocumento[]): void {
    const next: Record<number, DocSlot> = {};
    for (const d of lista) {
      if (d.tipo > 0 && d.kidDocumento > 0) {
        next[d.tipo] = { kidDocumento: d.kidDocumento, busy: false, downloading: false };
      }
    }
    this.slots.set(next);
    // Purgar nombres de tipos que ya no tienen documento (p. ej. tras eliminar),
    // para que una subida futura no herede el nombre del archivo anterior.
    this.fileNames.update((n) =>
      Object.fromEntries(Object.entries(n).filter(([tipo]) => next[Number(tipo)] != null)),
    );
  }

  /** Actualiza (inmutable) el slot de un tipo, creándolo si no existe. */
  private patchSlot(tipoId: number, patch: Partial<DocSlot>): void {
    this.slots.update((s) => {
      const cur = s[tipoId] ?? { kidDocumento: null, busy: false, downloading: false };
      return { ...s, [tipoId]: { ...cur, ...patch } };
    });
  }

  /** Memoriza el nombre real de un archivo para mostrarlo en la fila. */
  private recordarNombre(tipoId: number, nombre: string): void {
    this.fileNames.update((n) => ({ ...n, [tipoId]: nombre }));
  }

  private extraerError(err: unknown): string | null {
    const e = err as { error?: { errorMessage?: string; detail?: string; title?: string } };
    return e?.error?.errorMessage ?? e?.error?.detail ?? e?.error?.title ?? null;
  }
}
